import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, Square, Satellite, Wifi, WifiOff, Loader2, AlertTriangle, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { useOfflineLocationQueue } from '@/hooks/useOfflineLocationQueue';
import { useBeaconAlerts } from '@/hooks/useBeaconAlerts';
import TrackerSetup from '@/components/TrackerSetup';

const DISPLAY_FONT = { fontFamily: "'Bebas Neue', 'Oswald', sans-serif" };

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function distanceToTrack(lat, lon, points) {
  let min = Infinity;
  for (let i = 0; i < points.length; i++) {
    const d = haversine(lat, lon, points[i][0], points[i][1]);
    if (d < min) min = d;
  }
  return min;
}

const fmtDist = (m) => (m == null ? '—' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

function Stat({ label, value, unit, tone = 'text-gray-900', testId }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">{label}</p>
      <p className={`text-4xl leading-none mt-1 ${tone}`} style={DISPLAY_FONT}>
        {value}{unit && <span className="text-lg text-gray-400 ml-1">{unit}</span>}
      </p>
    </div>
  );
}

export default function BeaconMode() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [event, setEvent] = useState(null);
  const [trackPoints, setTrackPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  const [active, setActive] = useState(false);
  const [position, setPosition] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [distToTrack, setDistToTrack] = useState(null);
  const [lastSent, setLastSent] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [wakeLocked, setWakeLocked] = useState(false);
  const [alert, setAlert] = useState(null);
  const [stoppedPrompt, setStoppedPrompt] = useState(false);
  const [sosSent, setSosSent] = useState(false);
  const stopRef = useRef({ lat: null, lon: null, lastMovingAt: 0, prompted: false });

  const watchRef = useRef(null);
  const wakeRef = useRef(null);
  const lastSendRef = useRef(0);
  const offRouteRef = useRef(false);
  const overSpeedRef = useRef(false);

  const { isOnline, pendingCount, addToQueue, syncQueue } = useOfflineLocationQueue(registrationId);
  const { unlock, notify } = useBeaconAlerts();

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const h = { headers: { Authorization: `Bearer ${token}` } };
        const regs = await axios.get(`${API}/registrations/my`, h);
        const reg = regs.data.find(r => r.id === registrationId);
        if (!reg) throw new Error('Inscripción no encontrada');
        setRegistration(reg);
        const ev = await axios.get(`${API}/events/${reg.event_id}`);
        setEvent(ev.data);
        try {
          const gpx = await axios.get(`${API}/events/${reg.event_id}/gpx-coordinates`, h);
          setTrackPoints(gpx.data.coordinates || []);
        } catch {
          setTrackPoints([]);
        }
      } catch (e) {
        toast.error(e.message || 'Error cargando datos');
        navigate('/my-registrations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [registrationId, navigate]);

  const raiseAlert = useCallback((type, text) => {
    setAlert({ type, text, at: Date.now() });
    notify(type);
  }, [notify]);

  const send = useCallback(async (coords) => {
    const { latitude, longitude, altitude, accuracy, speed, heading } = coords;
    const data = { latitude, longitude, altitude, accuracy, speed, heading, timestamp: new Date().toISOString() };
    if (!navigator.onLine) {
      addToQueue(data);
      setLastSent(`${new Date().toLocaleTimeString()} (cola)`);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/live-location`, null, {
        params: { registration_id: registrationId, latitude, longitude, altitude, accuracy, speed, heading },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSent(new Date().toLocaleTimeString());
      setSendError(null);
      if (pendingCount > 0) syncQueue();
      (r.data?.alerts || []).forEach(a => {
        if (a === 'deviation') raiseAlert('deviation', 'Fuera de ruta');
        else if (a === 'speed') raiseAlert('speed', 'Exceso de velocidad');
        else if (a === 'return_to_track') raiseAlert('return_to_track', 'De vuelta en ruta');
        else if (a === 'speed_normalized') raiseAlert('speed_normalized', 'Velocidad normalizada');
      });
    } catch (e) {
      if (!navigator.onLine || e.message === 'Network Error') {
        addToQueue(data);
        setLastSent(`${new Date().toLocaleTimeString()} (cola)`);
      } else {
        setSendError(e.response?.data?.detail || e.message);
      }
    }
  }, [registrationId, addToQueue, syncQueue, pendingCount, raiseAlert]);

  const onPosition = useCallback((pos) => {
    const c = pos.coords;
    setPosition(c);
    setGpsError(null);

    const freeMode = event?.event_mode === 'free';
    if (!freeMode && trackPoints.length && event?.alert_deviation_enabled) {
      const d = distanceToTrack(c.latitude, c.longitude, trackPoints);
      setDistToTrack(d);
      const limit = (event.alert_deviation_distance_km || 10) * 1000;
      const out = d > limit;
      if (out && !offRouteRef.current) raiseAlert('deviation', 'Fuera de ruta');
      offRouteRef.current = out;
    } else if (trackPoints.length) {
      setDistToTrack(distanceToTrack(c.latitude, c.longitude, trackPoints));
    }

    if (!freeMode && event?.alert_speed_enabled && c.speed != null) {
      const over = c.speed * 3.6 > (event.alert_speed_limit_kmh || 120);
      if (over && !overSpeedRef.current) raiseAlert('speed', 'Exceso de velocidad');
      overSpeedRef.current = over;
    }

    const now = Date.now();
    const st = stopRef.current;
    if (st.lat == null) { st.lat = c.latitude; st.lon = c.longitude; st.lastMovingAt = now; }
    const movedM = haversine(st.lat, st.lon, c.latitude, c.longitude);
    if (movedM > 40 || (c.speed || 0) > 1) {
      st.lat = c.latitude; st.lon = c.longitude; st.lastMovingAt = now;
      if (st.prompted) { st.prompted = false; setStoppedPrompt(false); setSosSent(false); }
    } else if (!freeMode && !st.prompted && now - st.lastMovingAt > 10 * 60 * 1000) {
      st.prompted = true;
      setStoppedPrompt(true);
      notify('stopped');
    }
    if (now - lastSendRef.current >= 10000) {
      lastSendRef.current = now;
      send(c);
    }
  }, [trackPoints, event, send, raiseAlert, notify]);

  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeRef.current = await navigator.wakeLock.request('screen');
        setWakeLocked(true);
        wakeRef.current.addEventListener('release', () => setWakeLocked(false));
      }
    } catch {
      setWakeLocked(false);
    }
  }, []);

  useEffect(() => {
    const onVis = () => { if (active && document.visibilityState === 'visible' && !wakeRef.current) requestWakeLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [active, requestWakeLock]);

  const start = () => {
    if (!navigator.geolocation) { toast.error('GPS no disponible'); return; }
    unlock();
    requestWakeLock();
    setActive(true);
    lastSendRef.current = 0;
    stopRef.current = { lat: null, lon: null, lastMovingAt: Date.now(), prompted: false };
    setStoppedPrompt(false); setSosSent(false);
    watchRef.current = navigator.geolocation.watchPosition(onPosition, (e) => setGpsError(e.message), { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 });
    toast.success('Baliza activada');
  };

  const stop = useCallback(async () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    wakeRef.current?.release?.().catch(() => {});
    wakeRef.current = null;
    setWakeLocked(false);
    setActive(false);
    try {
      const token = localStorage.getItem('token');
      if (registration?.event_id) await axios.delete(`${API}/live-location/${registration.event_id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* ignore */ }
  }, [registration]);

  const sendSos = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/registrations/${registrationId}/sos`, {
        reason: 'stopped', latitude: position?.latitude ?? null, longitude: position?.longitude ?? null
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSosSent(true);
      notify('sos');
      toast.success('Aviso enviado al Rally Master');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar el aviso');
    }
  };

  const dismissStopped = () => {
    setStoppedPrompt(false);
    stopRef.current.lastMovingAt = Date.now();
    stopRef.current.prompted = false;
  };

  useEffect(() => () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    wakeRef.current?.release?.().catch(() => {});
  }, []);

  useEffect(() => {
    if (!active || watchRef.current == null) return;
    navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = navigator.geolocation.watchPosition(onPosition, (e) => setGpsError(e.message), { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 });
  }, [onPosition, active]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…</div>;
  }

  const speedKmh = position?.speed != null && position.speed >= 0 ? Math.round(position.speed * 3.6) : 0;
  const isWarning = alert && (alert.type === 'deviation' || alert.type === 'speed') && Date.now() - alert.at < 60000;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 px-4 py-6 pb-16" data-testid="beacon-mode">
      <div className="max-w-md mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => { stop(); navigate('/my-registrations'); }} className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 -ml-2 h-12" data-testid="beacon-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {isOnline ? <Wifi className="w-4 h-4 text-emerald-500" /> : <WifiOff className="w-4 h-4 text-amber-500" />}
            {pendingCount > 0 && <span className="text-amber-400" data-testid="beacon-pending">{pendingCount} en cola</span>}
            {wakeLocked && <Sun className="w-4 h-4 text-orange-500" title="Pantalla activa" />}
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-orange-600">Modo baliza</p>
          <h1 className="text-4xl leading-none tracking-wide" style={DISPLAY_FONT} data-testid="beacon-event-title">{event?.title}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Navega con tu GPS o app favorita. Deja esta pantalla abierta: envía tu posición y te avisa con sonido y vibración.
          </p>
        </div>

        {stoppedPrompt && (
          <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 space-y-3" data-testid="beacon-stopped-prompt">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-600 shrink-0" />
              <div>
                <p className="text-2xl leading-none text-amber-800" style={DISPLAY_FONT}>Llevas 10 min parado</p>
                <p className="text-sm text-amber-700">¿Va todo bien? Si necesitas ayuda avisamos al Rally Master con tu posición.</p>
              </div>
            </div>
            {sosSent ? (
              <p className="text-sm font-semibold text-emerald-700" data-testid="beacon-sos-sent">Aviso enviado. El Rally Master tiene tu posición.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={dismissStopped} className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold" data-testid="beacon-ok-btn">Todo bien</Button>
                <Button onClick={sendSos} className="h-14 bg-red-600 hover:bg-red-700 text-white text-base font-bold" data-testid="beacon-sos-btn">Necesito ayuda</Button>
              </div>
            )}
          </div>
        )}

        {isWarning && (
          <div className="rounded-xl border-2 border-red-400 bg-red-50 p-4 flex items-center gap-3 animate-pulse" data-testid="beacon-alert">
            <AlertTriangle className="w-8 h-8 text-red-600 shrink-0" />
            <div>
              <p className="text-2xl leading-none text-red-700" style={DISPLAY_FONT}>{alert.text}</p>
              <p className="text-xs text-red-500">{new Date(alert.at).toLocaleTimeString()}</p>
            </div>
          </div>
        )}

        <div className={`rounded-2xl border p-6 text-center ${active ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white shadow-sm'}`}>
          <p className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Velocidad</p>
          <p className="text-8xl leading-none mt-1" style={DISPLAY_FONT} data-testid="beacon-speed">{active ? speedKmh : '—'}</p>
          <p className="text-gray-500 text-sm -mt-1">km/h {event?.alert_speed_enabled && <span className="text-gray-400">· límite {event.alert_speed_limit_kmh}</span>}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat
            label="Distancia a ruta"
            value={trackPoints.length ? fmtDist(distToTrack) : 'n/d'}
            tone={offRouteRef.current ? 'text-red-600' : 'text-emerald-600'}
            testId="beacon-dist-track"
          />
          <Stat label="Precisión GPS" value={position ? Math.round(position.accuracy) : '—'} unit="m" testId="beacon-accuracy" />
          <Stat label="Último envío" value={lastSent ? lastSent.replace(' (cola)', '') : '—'} tone={lastSent?.includes('cola') ? 'text-amber-600' : 'text-gray-900'} testId="beacon-last-sent" />
          <Stat label="Estado" value={active ? (gpsError ? 'GPS' : 'ON') : 'OFF'} tone={active ? (gpsError ? 'text-amber-600' : 'text-emerald-600') : 'text-gray-400'} testId="beacon-status" />
        </div>

        {(gpsError || sendError) && (
          <p className="text-xs text-amber-700 flex items-center gap-2" data-testid="beacon-error"><AlertTriangle className="w-3.5 h-3.5" /> {gpsError || sendError}</p>
        )}

        {active ? (
          <Button onClick={stop} className="w-full h-16 text-lg bg-gray-800 hover:bg-gray-900 text-white uppercase tracking-wider" data-testid="beacon-stop-btn">
            <Square className="w-5 h-5 mr-2" /> Detener baliza
          </Button>
        ) : (
          <Button onClick={start} className="w-full h-16 text-lg bg-orange-500 hover:bg-orange-600 text-white uppercase tracking-wider font-bold shadow-lg shadow-orange-500/30" data-testid="beacon-start-btn">
            <Play className="w-5 h-5 mr-2" /> Activar baliza
          </Button>
        )}

        <p className="text-xs text-gray-500 flex items-start gap-2">
          <Satellite className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          La pantalla se mantiene encendida mientras la baliza está activa. Baja el brillo para ahorrar batería. Si bloqueas el móvil o cambias de app, el navegador detendrá el GPS.
        </p>

        <TrackerSetup registrationId={registrationId} />
      </div>
    </div>
  );
}
